import React, {
    useEffect,
    useMemo,
    useState,
} from "react";

import {
    useDispatch,
    useSelector,
} from "react-redux";

import {
    useHistory,
    useParams,
} from "react-router-dom";

import {
    FiArrowLeft,
    FiBriefcase,
    FiCalendar,
    FiCheckCircle,
    FiClock,
    FiCreditCard,
    FiEdit2,
    FiMonitor,
    FiPackage,
    FiRefreshCw,
    FiShoppingBag,
    FiUsers,
} from "react-icons/fi";

import useSwal
    from "../../../../../hooks/useSwal";



/*
|--------------------------------------------------------------------------
| BUSINESS
|--------------------------------------------------------------------------
*/

import {
    fetchPlatformBusinessById,

    selectSelectedPlatformBusiness,
    selectPlatformBusinessDetailsLoading,

    clearSelectedPlatformBusiness,
} from "../../../../../store/platformBusiness/platformBusinessSlice";


/*
|--------------------------------------------------------------------------
| SUBSCRIPTION PLAN MASTER
|--------------------------------------------------------------------------
*/

import {
    fetchPlatformSubscriptionPlans,

    selectPlatformSubscriptionPlans,
    selectPlatformSubscriptionPlanLoading,
} from "../../../../../store/platformSubscriptionPlan/platformSubscriptionPlanSlice";


/*
|--------------------------------------------------------------------------
| TENANT SUBSCRIPTION
|--------------------------------------------------------------------------
*/

import {
    assignPlatformTenantSubscriptionPlan,

    clearPlatformTenantSubscriptionAssignment,

    selectPlatformTenantSubscriptionAssignment,
} from "../../../../../store/platformSubscriptionPlan/platformTenantSubscriptionSlice";


/*
|--------------------------------------------------------------------------
| COMPONENTS
|--------------------------------------------------------------------------
*/

import {
    BusinessStatusBadge,
    BusinessFeatureTags,
    BusinessModal,
} from "../business/components";


/*
|--------------------------------------------------------------------------
| CSS
|--------------------------------------------------------------------------
*/

import "../business/PlatformBusiness.css";

import "./PlatformSubscription.css";


/*
|--------------------------------------------------------------------------
| FORMAT DATE
|--------------------------------------------------------------------------
*/

const formatDate =
    (
        value
    ) => {

        if (
            !value
        ) {

            return "-";

        }


        const date =
            new Date(
                value
            );


        if (
            Number.isNaN(
                date.getTime()
            )
        ) {

            return "-";

        }


        return date
            .toLocaleDateString(
                "en-IN",
                {
                    day:
                        "2-digit",

                    month:
                        "short",

                    year:
                        "numeric",
                }
            );

    };


/*
|--------------------------------------------------------------------------
| FORMAT PRICE
|--------------------------------------------------------------------------
*/

const formatPrice =
    (
        value
    ) => {

        return Number(
            value ||
            0
        )
            .toLocaleString(
                "en-IN"
            );

    };


/*
|--------------------------------------------------------------------------
| FORMAT VALUE
|--------------------------------------------------------------------------
*/

const formatEnum =
    (
        value
    ) => {

        if (
            !value
        ) {

            return "-";

        }


        return String(
            value
        )
            .trim()
            .toLowerCase()
            .replace(
                /_/g,
                " "
            )
            .replace(
                /\b\w/g,
                (
                    char
                ) =>
                    char
                        .toUpperCase()
            );

    };


/*
|--------------------------------------------------------------------------
| DETAIL ITEM
|--------------------------------------------------------------------------
*/

const DetailItem =
    ({
        icon: Icon,

        label,

        value,

        children,
    }) => {

        return (

            <div className="platform-subscription-detail-item">

                <div className="platform-subscription-detail-icon">

                    {
                        Icon
                            ?
                            <Icon />
                            :
                            null
                    }

                </div>


                <div className="platform-subscription-detail-content">

                    <span>
                        {
                            label
                        }
                    </span>


                    {
                        children
                            ?
                            children
                            :
                            (
                                <strong>
                                    {
                                        value ||
                                        "-"
                                    }
                                </strong>
                            )
                    }

                </div>

            </div>

        );

    };


/*
|--------------------------------------------------------------------------
| PLATFORM SUBSCRIPTION DETAILS
|--------------------------------------------------------------------------
*/

const PlatformSubscriptionDetails =
    () => {

        const dispatch =
            useDispatch();


        const history =
            useHistory();


        const {
            id,
        } =
            useParams();


        const swal =
            useSwal();


        /*
        |--------------------------------------------------------------------------
        | BUSINESS
        |--------------------------------------------------------------------------
        */

        const business =
            useSelector(
                selectSelectedPlatformBusiness
            );


        const loading =
            useSelector(
                selectPlatformBusinessDetailsLoading
            );


        /*
        |--------------------------------------------------------------------------
        | PLAN MASTER
        |--------------------------------------------------------------------------
        */

        const subscriptionPlans =
            useSelector(
                selectPlatformSubscriptionPlans
            );


        const plansLoading =
            useSelector(
                selectPlatformSubscriptionPlanLoading
            );


        /*
        |--------------------------------------------------------------------------
        | ASSIGNMENT
        |--------------------------------------------------------------------------
        */

        const assignPlanState =
            useSelector(
                selectPlatformTenantSubscriptionAssignment
            );


        const assignPlanLoading =
            Boolean(
                assignPlanState
                    ?.loading
            );


        /*
        |--------------------------------------------------------------------------
        | MODAL
        |--------------------------------------------------------------------------
        */

        const [
            showEditModal,
            setShowEditModal,
        ] =
            useState(
                false
            );


        /*
        |--------------------------------------------------------------------------
        | FORM
        |--------------------------------------------------------------------------
        */

        const [
            subscriptionForm,
            setSubscriptionForm,
        ] =
            useState({

                planId:
                    "",

                billingCycle:
                    "MONTHLY",

                assignmentReason:
                    "",

            });


        /*
        |--------------------------------------------------------------------------
        | LOAD BUSINESS
        |--------------------------------------------------------------------------
        */

        useEffect(
            () => {

                if (
                    id
                ) {

                    dispatch(
                        fetchPlatformBusinessById(
                            id
                        )
                    );

                }


                return () => {

                    dispatch(
                        clearSelectedPlatformBusiness()
                    );


                    dispatch(
                        clearPlatformTenantSubscriptionAssignment()
                    );

                };

            },
            [
                dispatch,
                id,
            ]
        );


        /*
        |--------------------------------------------------------------------------
        | CURRENT SUBSCRIPTION
        |--------------------------------------------------------------------------
        */

        const currentSubscription =
            business
                ?.currentSubscription ||
            null;


        /*
        |--------------------------------------------------------------------------
        | CURRENT PLAN
        |--------------------------------------------------------------------------
        */

        const currentPlanName =
            currentSubscription
                ?.planName ||
            currentSubscription
                ?.planSnapshot
                ?.name ||
            currentSubscription
                ?.planCode ||
            "No Plan";


        const currentPlanCode =
            currentSubscription
                ?.planCode ||
            currentSubscription
                ?.planSnapshot
                ?.code ||
            "";


        /*
        |--------------------------------------------------------------------------
        | STATUS
        |--------------------------------------------------------------------------
        */

        const subscriptionStatus =
            String(
                currentSubscription
                    ?.status ||
                business
                    ?.subscriptionStatus ||
                "NONE"
            )
                .trim()
                .toUpperCase();


        /*
        |--------------------------------------------------------------------------
        | EXPIRY
        |--------------------------------------------------------------------------
        */

        const subscriptionExpiry =
            currentSubscription
                ?.endsAt ||
            business
                ?.trialEndsAt ||
            null;


        /*
        |--------------------------------------------------------------------------
        | DAYS REMAINING
        |--------------------------------------------------------------------------
        */

        const remainingInfo =
            useMemo(
                () => {

                    if (
                        !subscriptionExpiry
                    ) {

                        return {
                            expired:
                                false,

                            daysRemaining:
                                null,
                        };

                    }


                    const end =
                        new Date(
                            subscriptionExpiry
                        );


                    if (
                        Number.isNaN(
                            end.getTime()
                        )
                    ) {

                        return {
                            expired:
                                false,

                            daysRemaining:
                                null,
                        };

                    }


                    const diff =
                        end.getTime() -
                        Date.now();


                    const days =
                        Math.ceil(
                            diff /
                            (
                                1000 *
                                60 *
                                60 *
                                24
                            )
                        );


                    return {

                        expired:
                            days <
                            0,

                        daysRemaining:
                            Math.max(
                                days,
                                0
                            ),

                    };

                },
                [
                    subscriptionExpiry,
                ]
            );


        /*
        |--------------------------------------------------------------------------
        | ENABLED CURRENT MODULES
        |--------------------------------------------------------------------------
        */

        const currentModules =
            useMemo(
                () => {

                    if (
                        !Array.isArray(
                            currentSubscription
                                ?.modules
                        )
                    ) {

                        return [];

                    }


                    return currentSubscription
                        .modules
                        .filter(
                            (
                                module
                            ) =>
                                module
                                    ?.enabled ===
                                true
                        );

                },
                [
                    currentSubscription,
                ]
            );


        /*
        |--------------------------------------------------------------------------
        | SELECTED PLAN
        |--------------------------------------------------------------------------
        */

        const selectedSubscriptionPlan =
            useMemo(
                () => {

                    if (
                        !subscriptionForm
                            .planId
                    ) {

                        return null;

                    }


                    return (
                        subscriptionPlans
                            .find(
                                (
                                    plan
                                ) =>
                                    String(
                                        plan?._id
                                    ) ===
                                    String(
                                        subscriptionForm
                                            .planId
                                    )
                            ) ||
                        null
                    );

                },
                [
                    subscriptionPlans,
                    subscriptionForm
                        .planId,
                ]
            );


        /*
        |--------------------------------------------------------------------------
        | SELECTED PLAN TYPE
        |--------------------------------------------------------------------------
        */

        const selectedPlanType =
            String(
                selectedSubscriptionPlan
                    ?.planType ||
                ""
            )
                .trim()
                .toUpperCase();


        /*
        |--------------------------------------------------------------------------
        | AVAILABLE BILLING CYCLES
        |--------------------------------------------------------------------------
        */

        const availableBillingCycles =
            useMemo(
                () => {

                    if (
                        !selectedSubscriptionPlan
                    ) {

                        return [];

                    }


                    if (
                        selectedPlanType ===
                        "TRIAL"
                    ) {

                        return [
                            "TRIAL",
                        ];

                    }


                    const cycles =
                        Array.isArray(
                            selectedSubscriptionPlan
                                ?.allowedBillingCycles
                        )
                            ?
                            selectedSubscriptionPlan
                                .allowedBillingCycles
                                .map(
                                    (
                                        cycle
                                    ) =>
                                        String(
                                            cycle
                                        )
                                            .trim()
                                            .toUpperCase()
                                )
                            :
                            [];


                    if (
                        cycles.length
                    ) {

                        return cycles;

                    }


                    return [
                        "MONTHLY",
                        "YEARLY",
                    ];

                },
                [
                    selectedSubscriptionPlan,
                    selectedPlanType,
                ]
            );


        /*
        |--------------------------------------------------------------------------
        | SELECTED PLAN MODULES
        |--------------------------------------------------------------------------
        */

        const selectedPlanModules =
            useMemo(
                () => {

                    if (
                        !Array.isArray(
                            selectedSubscriptionPlan
                                ?.modules
                        )
                    ) {

                        return [];

                    }


                    return selectedSubscriptionPlan
                        .modules
                        .filter(
                            (
                                module
                            ) =>
                                module
                                    ?.enabled ===
                                true
                        );

                },
                [
                    selectedSubscriptionPlan,
                ]
            );


        /*
        |--------------------------------------------------------------------------
        | REFRESH
        |--------------------------------------------------------------------------
        */

        const handleRefresh =
            () => {

                if (
                    !id
                ) {

                    return;

                }


                dispatch(
                    fetchPlatformBusinessById(
                        id
                    )
                );

            };


        /*
        |--------------------------------------------------------------------------
        | OPEN MANAGE SUBSCRIPTION
        |--------------------------------------------------------------------------
        */

        const handleOpenEdit =
            async () => {

                if (
                    !business
                ) {

                    return;

                }


                dispatch(
                    clearPlatformTenantSubscriptionAssignment()
                );


                /*
                |--------------------------------------------------------------------------
                | LOAD ACTIVE PLANS
                |--------------------------------------------------------------------------
                */

                const result =
                    await dispatch(
                        fetchPlatformSubscriptionPlans({
                            status:
                                "ACTIVE",
                        })
                    );


                /*
                |--------------------------------------------------------------------------
                | CURRENT PLAN
                |--------------------------------------------------------------------------
                |
                | Preselect existing plan when available.
                |
                |--------------------------------------------------------------------------
                */

                const existingPlanId =
                    currentSubscription
                        ?.planId
                        ?._id ||
                    currentSubscription
                        ?.planId ||
                    "";


                let billingCycle =
                    currentSubscription
                        ?.billingCycle ||
                    "MONTHLY";


                if (
                    String(
                        currentSubscription
                            ?.planType ||
                        ""
                    )
                        .trim()
                        .toUpperCase() ===
                    "TRIAL"
                ) {

                    billingCycle =
                        "TRIAL";

                }


                setSubscriptionForm({

                    planId:
                        String(
                            existingPlanId ||
                            ""
                        ),

                    billingCycle,

                    assignmentReason:
                        "",

                });


                /*
                |--------------------------------------------------------------------------
                | OPEN
                |--------------------------------------------------------------------------
                */

                setShowEditModal(
                    true
                );


                return result;

            };


        /*
        |--------------------------------------------------------------------------
        | CLOSE
        |--------------------------------------------------------------------------
        */

        const handleCloseEdit =
            () => {

                if (
                    assignPlanLoading
                ) {

                    return;

                }


                setShowEditModal(
                    false
                );


                setSubscriptionForm({

                    planId:
                        "",

                    billingCycle:
                        "MONTHLY",

                    assignmentReason:
                        "",

                });


                dispatch(
                    clearPlatformTenantSubscriptionAssignment()
                );

            };


        /*
        |--------------------------------------------------------------------------
        | PLAN CHANGE
        |--------------------------------------------------------------------------
        */

        const handlePlanChange =
            (
                event
            ) => {

                const planId =
                    event
                        .target
                        .value;


                const plan =
                    subscriptionPlans
                        .find(
                            (
                                item
                            ) =>
                                String(
                                    item?._id
                                ) ===
                                String(
                                    planId
                                )
                        );


                const planType =
                    String(
                        plan
                            ?.planType ||
                        ""
                    )
                        .trim()
                        .toUpperCase();


                let billingCycle =
                    "MONTHLY";


                /*
                |--------------------------------------------------------------------------
                | TRIAL
                |--------------------------------------------------------------------------
                */

                if (
                    planType ===
                    "TRIAL"
                ) {

                    billingCycle =
                        "TRIAL";

                } else {

                    const cycles =
                        Array.isArray(
                            plan
                                ?.allowedBillingCycles
                        )
                            ?
                            plan
                                .allowedBillingCycles
                                .map(
                                    (
                                        cycle
                                    ) =>
                                        String(
                                            cycle
                                        )
                                            .trim()
                                            .toUpperCase()
                                )
                            :
                            [];


                    if (
                        cycles.includes(
                            "MONTHLY"
                        )
                    ) {

                        billingCycle =
                            "MONTHLY";

                    } else if (
                        cycles.includes(
                            "YEARLY"
                        )
                    ) {

                        billingCycle =
                            "YEARLY";

                    }

                }


                setSubscriptionForm(
                    (
                        previous
                    ) => ({

                        ...previous,

                        planId,

                        billingCycle,

                    })
                );

            };


        /*
        |--------------------------------------------------------------------------
        | FORM CHANGE
        |--------------------------------------------------------------------------
        */

        const handleSubscriptionChange =
            (
                event
            ) => {

                const {
                    name,
                    value,
                } =
                    event.target;


                setSubscriptionForm(
                    (
                        previous
                    ) => ({

                        ...previous,

                        [name]:
                            value,

                    })
                );

            };


        /*
        |--------------------------------------------------------------------------
        | SAVE
        |--------------------------------------------------------------------------
        */

        const handleSave =
            async (
                event
            ) => {

                event
                    .preventDefault();


                if (
                    !id ||
                    !business
                ) {

                    return;

                }


                /*
                |--------------------------------------------------------------------------
                | PLAN
                |--------------------------------------------------------------------------
                */

                if (
                    !subscriptionForm
                        .planId
                ) {

                    await swal.warning({

                        title:
                            "Select Subscription Plan",

                        text:
                            "Please select a subscription plan.",

                    });


                    return;

                }


                /*
                |--------------------------------------------------------------------------
                | BILLING
                |--------------------------------------------------------------------------
                */

                if (
                    selectedPlanType !==
                        "TRIAL" &&
                    ![
                        "MONTHLY",
                        "YEARLY",
                    ].includes(
                        subscriptionForm
                            .billingCycle
                    )
                ) {

                    await swal.warning({

                        title:
                            "Select Billing Cycle",

                        text:
                            "Please select monthly or yearly billing.",

                    });


                    return;

                }


                /*
                |--------------------------------------------------------------------------
                | CONFIRM
                |--------------------------------------------------------------------------
                */

                const confirmation =
                    await swal.confirm({

                        title:
                            currentSubscription
                                ?
                                "Change Subscription Plan?"
                                :
                                "Assign Subscription Plan?",

                        text:
                            `${
                                selectedSubscriptionPlan
                                    ?.name ||
                                "Selected plan"
                            } will be assigned to ${
                                business
                                    ?.name ||
                                "this business"
                            }. Customer payment will not be required.`,

                        confirmButtonText:
                            currentSubscription
                                ?
                                "Change Plan"
                                :
                                "Assign Plan",

                    });


                if (
                    confirmation
                        ?.isConfirmed ===
                    false
                ) {

                    return;

                }


                /*
                |--------------------------------------------------------------------------
                | ASSIGN
                |--------------------------------------------------------------------------
                */

                const result =
                    await dispatch(
                        assignPlatformTenantSubscriptionPlan({

                            tenantId:
                                id,

                            planId:
                                subscriptionForm
                                    .planId,

                            billingCycle:
                                selectedPlanType ===
                                    "TRIAL"
                                    ?
                                    "TRIAL"
                                    :
                                    subscriptionForm
                                        .billingCycle,

                            assignmentReason:
                                subscriptionForm
                                    .assignmentReason,

                        })
                    );


                /*
                |--------------------------------------------------------------------------
                | ERROR
                |--------------------------------------------------------------------------
                */

                if (
                    !assignPlatformTenantSubscriptionPlan
                        .fulfilled
                        .match(
                            result
                        )
                ) {

                    await swal.error({

                        title:
                            "Unable to Assign Plan",

                        text:
                            result
                                ?.payload
                                ?.message ||
                            "Unable to assign subscription plan.",

                    });


                    return;

                }


                /*
                |--------------------------------------------------------------------------
                | SUCCESS
                |--------------------------------------------------------------------------
                */

                await swal.success({

                    title:
                        currentSubscription
                            ?
                            "Plan Changed"
                            :
                            "Plan Assigned",

                    text:
                        `${
                            selectedSubscriptionPlan
                                ?.name ||
                            "Subscription plan"
                        } has been assigned successfully.`,

                });


                /*
                |--------------------------------------------------------------------------
                | CLOSE
                |--------------------------------------------------------------------------
                */

                setShowEditModal(
                    false
                );


                setSubscriptionForm({

                    planId:
                        "",

                    billingCycle:
                        "MONTHLY",

                    assignmentReason:
                        "",

                });


                dispatch(
                    clearPlatformTenantSubscriptionAssignment()
                );


                /*
                |--------------------------------------------------------------------------
                | REFRESH
                |--------------------------------------------------------------------------
                */

                dispatch(
                    fetchPlatformBusinessById(
                        id
                    )
                );

            };


        /*
        |--------------------------------------------------------------------------
        | LOADING
        |--------------------------------------------------------------------------
        */

        if (
            loading &&
            !business
        ) {

            return (


                    <div className="platform-subscription-details-state">

                        <div className="platform-business-loader" />


                        <strong>
                            Loading Subscription
                        </strong>


                        <span>
                            Fetching subscription information...
                        </span>

                    </div>


            );

        }


        /*
        |--------------------------------------------------------------------------
        | NOT FOUND
        |--------------------------------------------------------------------------
        */

        if (
            !business &&
            !loading
        ) {

            return (


                    <div className="platform-subscription-details-state">

                        <FiShoppingBag />


                        <h2>
                            Subscription Not Found
                        </h2>


                        <p>
                            The requested business subscription could not be loaded.
                        </p>


                        <button
                            type="button"

                            onClick={
                                () =>
                                    history.push(
                                        "/platform/subscriptions"
                                    )
                            }
                        >

                            <FiArrowLeft />

                            Back to Subscriptions

                        </button>

                    </div>

            );

        }


        /*
        |--------------------------------------------------------------------------
        | UI
        |--------------------------------------------------------------------------
        */

        return (


                <div className="platform-subscription-details-page">

                    {/* ============================================================
                        HEADER
                    ============================================================ */}

                    <header className="platform-subscription-details-header">

                        <div>

                            <button
                                type="button"

                                className="platform-subscription-back"

                                onClick={
                                    () =>
                                        history.push(
                                            "/platform/subscriptions"
                                        )
                                }
                            >

                                <FiArrowLeft />

                                Subscriptions

                            </button>


                            <div className="platform-subscription-details-title-row">

                                <div className="platform-subscription-details-avatar">

                                    {
                                        String(
                                            business
                                                ?.name ||
                                            "B"
                                        )
                                            .charAt(
                                                0
                                            )
                                            .toUpperCase()
                                    }

                                </div>


                                <div>

                                    <div className="platform-subscription-details-title">

                                        <h1>
                                            {
                                                business
                                                    ?.name ||
                                                "Business Subscription"
                                            }
                                        </h1>


                                        <BusinessStatusBadge
                                            status={
                                                subscriptionStatus
                                            }
                                        />

                                    </div>


                                    <p>

                                        {
                                            business
                                                ?.code ||
                                            business
                                                ?.subdomain ||
                                            "-"
                                        }

                                    </p>

                                </div>

                            </div>

                        </div>


                        <div className="platform-subscription-details-actions">

                            <button
                                type="button"

                                className="refresh"

                                onClick={
                                    handleRefresh
                                }

                                disabled={
                                    loading
                                }
                            >

                                <FiRefreshCw
                                    className={
                                        loading
                                            ?
                                            "platform-subscription-spin"
                                            :
                                            ""
                                    }
                                />

                                Refresh

                            </button>


                            <button
                                type="button"

                                className="edit"

                                onClick={
                                    handleOpenEdit
                                }

                                disabled={
                                    assignPlanLoading
                                }
                            >

                                <FiEdit2 />

                                {
                                    currentSubscription
                                        ?
                                        "Change Plan"
                                        :
                                        "Assign Plan"
                                }

                            </button>

                        </div>

                    </header>


                    {/* ============================================================
                        OVERVIEW
                    ============================================================ */}

                    <section className="platform-subscription-details-overview">

                        <article>

                            <span>
                                Current Plan
                            </span>

                            <strong>
                                {
                                    currentPlanName
                                }
                            </strong>


                            {
                                currentPlanCode
                                    ?
                                    (
                                        <small>
                                            {
                                                currentPlanCode
                                            }
                                        </small>
                                    )
                                    :
                                    null
                            }

                        </article>


                        <article>

                            <span>
                                Subscription
                            </span>

                            <BusinessStatusBadge
                                status={
                                    subscriptionStatus
                                }
                            />

                        </article>


                        <article>

                            <span>
                                Billing Cycle
                            </span>

                            <strong>
                                {
                                    formatEnum(
                                        currentSubscription
                                            ?.billingCycle
                                    )
                                }
                            </strong>

                        </article>


                        <article>

                            <span>
                                Expires
                            </span>

                            <strong>
                                {
                                    formatDate(
                                        subscriptionExpiry
                                    )
                                }
                            </strong>

                        </article>


                        <article>

                            <span>
                                Remaining
                            </span>

                            <strong>

                                {
                                    remainingInfo
                                        .daysRemaining ===
                                    null
                                        ?
                                        "-"
                                        :
                                        remainingInfo
                                            .expired
                                            ?
                                            "Expired"
                                            :
                                            `${remainingInfo.daysRemaining} day(s)`
                                }

                            </strong>

                        </article>

                    </section>


                    {/* ============================================================
                        DETAILS GRID
                    ============================================================ */}

                    <div className="platform-subscription-details-grid">

                        {/* ========================================================
                            SUBSCRIPTION
                        ======================================================== */}

                        <section className="platform-subscription-details-card">

                            <div className="platform-subscription-details-card-header">

                                <div>
                                    <FiShoppingBag />
                                </div>


                                <div>

                                    <h2>
                                        Subscription Information
                                    </h2>


                                    <p>
                                        Current assigned subscription plan and lifecycle.
                                    </p>

                                </div>

                            </div>


                            <div className="platform-subscription-detail-list">

                                <DetailItem
                                    icon={
                                        FiPackage
                                    }

                                    label="Plan"

                                    value={
                                        currentPlanName
                                    }
                                />


                                <DetailItem
                                    icon={
                                        FiShoppingBag
                                    }

                                    label="Plan Code"

                                    value={
                                        currentPlanCode
                                    }
                                />


                                <DetailItem
                                    icon={
                                        FiCheckCircle
                                    }

                                    label="Subscription Status"
                                >

                                    <BusinessStatusBadge
                                        status={
                                            subscriptionStatus
                                        }
                                    />

                                </DetailItem>


                                <DetailItem
                                    icon={
                                        FiClock
                                    }

                                    label="Billing Cycle"

                                    value={
                                        formatEnum(
                                            currentSubscription
                                                ?.billingCycle
                                        )
                                    }
                                />


                                <DetailItem
                                    icon={
                                        FiCalendar
                                    }

                                    label="Starts"

                                    value={
                                        formatDate(
                                            currentSubscription
                                                ?.startsAt
                                        )
                                    }
                                />


                                <DetailItem
                                    icon={
                                        FiCalendar
                                    }

                                    label="Expires"

                                    value={
                                        formatDate(
                                            subscriptionExpiry
                                        )
                                    }
                                />


                                <DetailItem
                                    icon={
                                        FiClock
                                    }

                                    label="Days Remaining"

                                    value={
                                        remainingInfo
                                            .daysRemaining ===
                                        null
                                            ?
                                            "-"
                                            :
                                            remainingInfo
                                                .expired
                                                ?
                                                "Expired"
                                                :
                                                `${remainingInfo.daysRemaining} day(s)`
                                    }
                                />

                            </div>

                        </section>


                        {/* ========================================================
                            ASSIGNMENT & PAYMENT
                        ======================================================== */}

                        <section className="platform-subscription-details-card">

                            <div className="platform-subscription-details-card-header">

                                <div>
                                    <FiCreditCard />
                                </div>


                                <div>

                                    <h2>
                                        Assignment & Payment
                                    </h2>


                                    <p>
                                        How this plan was assigned and its payment state.
                                    </p>

                                </div>

                            </div>


                            <div className="platform-subscription-detail-list">

                                <DetailItem
                                    icon={
                                        FiCheckCircle
                                    }

                                    label="Assigned Through"

                                    value={
                                        formatEnum(
                                            currentSubscription
                                                ?.assignmentSource
                                        )
                                    }
                                />


                                <DetailItem
                                    icon={
                                        FiCreditCard
                                    }

                                    label="Payment Status"

                                    value={
                                        formatEnum(
                                            currentSubscription
                                                ?.paymentStatus
                                        )
                                    }
                                />


                                <DetailItem
                                    icon={
                                        FiCreditCard
                                    }

                                    label="Payment Required"

                                    value={
                                        currentSubscription
                                            ?
                                            (
                                                currentSubscription
                                                    ?.paymentRequired
                                                    ?
                                                    "Yes"
                                                    :
                                                    "No"
                                            )
                                            :
                                            "-"
                                    }
                                />


                                <DetailItem
                                    icon={
                                        FiCreditCard
                                    }

                                    label="Plan Amount"

                                    value={
                                        currentSubscription
                                            ?
                                            `${
                                                currentSubscription
                                                    ?.currency ||
                                                "INR"
                                            } ${formatPrice(
                                                currentSubscription
                                                    ?.amount
                                            )}`
                                            :
                                            "-"
                                    }
                                />


                                <DetailItem
                                    icon={
                                        FiCreditCard
                                    }

                                    label="Paid Amount"

                                    value={
                                        currentSubscription
                                            ?
                                            `${
                                                currentSubscription
                                                    ?.currency ||
                                                "INR"
                                            } ${formatPrice(
                                                currentSubscription
                                                    ?.paidAmount
                                            )}`
                                            :
                                            "-"
                                    }
                                />


                                <DetailItem
                                    icon={
                                        FiCheckCircle
                                    }

                                    label="Assignment Reason"

                                    value={
                                        currentSubscription
                                            ?.assignmentReason ||
                                        "-"
                                    }
                                />

                            </div>

                        </section>


                        {/* ========================================================
                            FEATURES
                        ======================================================== */}

                        <section className="platform-subscription-details-card">

                            <div className="platform-subscription-details-card-header">

                                <div>
                                    <FiMonitor />
                                </div>


                                <div>

                                    <h2>
                                        Enabled Features
                                    </h2>


                                    <p>
                                        Platform capabilities included in this subscription.
                                    </p>

                                </div>

                            </div>


                            <div className="platform-subscription-feature-details">

                                <div
                                    className={
                                        (
                                            currentSubscription
                                                ?.features
                                                ?.storefrontEnabled ??
                                            business
                                                ?.storefrontEnabled
                                        )
                                            ?
                                            "enabled"
                                            :
                                            "disabled"
                                    }
                                >

                                    <FiMonitor />


                                    <div>

                                        <strong>
                                            Storefront
                                        </strong>


                                        <span>

                                            {
                                                (
                                                    currentSubscription
                                                        ?.features
                                                        ?.storefrontEnabled ??
                                                    business
                                                        ?.storefrontEnabled
                                                )
                                                    ?
                                                    "Enabled"
                                                    :
                                                    "Disabled"
                                            }

                                        </span>

                                    </div>

                                </div>


                                <div
                                    className={
                                        (
                                            currentSubscription
                                                ?.features
                                                ?.marketplaceEnabled ??
                                            business
                                                ?.marketplaceEnabled
                                        )
                                            ?
                                            "enabled"
                                            :
                                            "disabled"
                                    }
                                >

                                    <FiShoppingBag />


                                    <div>

                                        <strong>
                                            Marketplace
                                        </strong>


                                        <span>

                                            {
                                                (
                                                    currentSubscription
                                                        ?.features
                                                        ?.marketplaceEnabled ??
                                                    business
                                                        ?.marketplaceEnabled
                                                )
                                                    ?
                                                    "Enabled"
                                                    :
                                                    "Disabled"
                                            }

                                        </span>

                                    </div>

                                </div>

                            </div>


                            <div className="platform-subscription-feature-tags">

                                <BusinessFeatureTags
                                    storefrontEnabled={
                                        currentSubscription
                                            ?.features
                                            ?.storefrontEnabled ??
                                        business
                                            ?.storefrontEnabled
                                    }

                                    marketplaceEnabled={
                                        currentSubscription
                                            ?.features
                                            ?.marketplaceEnabled ??
                                        business
                                            ?.marketplaceEnabled
                                    }
                                />

                            </div>

                        </section>


                        {/* ========================================================
                            LIMITS
                        ======================================================== */}

                        <section className="platform-subscription-details-card">

                            <div className="platform-subscription-details-card-header">

                                <div>
                                    <FiUsers />
                                </div>


                                <div>

                                    <h2>
                                        Plan Limits
                                    </h2>


                                    <p>
                                        Resource limits assigned with the current plan.
                                    </p>

                                </div>

                            </div>


                            <div className="platform-subscription-detail-list">

                                <DetailItem
                                    icon={
                                        FiUsers
                                    }

                                    label="Max Employees"

                                    value={
                                        currentSubscription
                                            ?.limits
                                            ?.maxEmployees ===
                                        null
                                            ?
                                            "Unlimited"
                                            :
                                            currentSubscription
                                                ?.limits
                                                ?.maxEmployees
                                    }
                                />


                                <DetailItem
                                    icon={
                                        FiPackage
                                    }

                                    label="Product Listings"

                                    value={
                                        currentSubscription
                                            ?.limits
                                            ?.maxProductListings ===
                                        null
                                            ?
                                            "Unlimited"
                                            :
                                            currentSubscription
                                                ?.limits
                                                ?.maxProductListings
                                    }
                                />


                                <DetailItem
                                    icon={
                                        FiBriefcase
                                    }

                                    label="Branches"

                                    value={
                                        currentSubscription
                                            ?.limits
                                            ?.maxBranches
                                    }
                                />


                                <DetailItem
                                    icon={
                                        FiPackage
                                    }

                                    label="Storage"

                                    value={
                                        currentSubscription
                                            ?.limits
                                            ?.maxStorageGB ===
                                        null
                                            ?
                                            "Unlimited"
                                            :
                                            currentSubscription
                                                ?.limits
                                                ?.maxStorageGB !==
                                            undefined
                                                ?
                                                `${currentSubscription.limits.maxStorageGB} GB`
                                                :
                                                "-"
                                    }
                                />

                            </div>

                        </section>


                        {/* ========================================================
                            MODULE ACCESS
                        ======================================================== */}

                        <section className="platform-subscription-details-card platform-subscription-details-wide">

                            <div className="platform-subscription-details-card-header">

                                <div>
                                    <FiPackage />
                                </div>


                                <div>

                                    <h2>
                                        Included Modules
                                    </h2>


                                    <p>
                                        Modules currently available through the assigned plan.
                                    </p>

                                </div>

                            </div>


                            {
                                currentModules.length
                                    ?
                                    (
                                        <div className="platform-subscription-module-grid">

                                            {
                                                currentModules
                                                    .map(
                                                        (
                                                            module
                                                        ) => (

                                                            <div
                                                                key={
                                                                    module
                                                                        ?.code
                                                                }

                                                                className="platform-subscription-module-item"
                                                            >

                                                                <FiCheckCircle />


                                                                <div>

                                                                    <strong>
                                                                        {
                                                                            module
                                                                                ?.name ||
                                                                            module
                                                                                ?.code
                                                                        }
                                                                    </strong>


                                                                    <span>
                                                                        {
                                                                            module
                                                                                ?.code
                                                                        }
                                                                    </span>


                                                                    {
                                                                        Array.isArray(
                                                                            module
                                                                                ?.permissions
                                                                        )
                                                                            ?
                                                                            (
                                                                                <small>
                                                                                    {
                                                                                        module
                                                                                            .permissions
                                                                                            .length
                                                                                    } permission(s)
                                                                                </small>
                                                                            )
                                                                            :
                                                                            null
                                                                    }

                                                                </div>

                                                            </div>

                                                        )
                                                    )
                                            }

                                        </div>
                                    )
                                    :
                                    (
                                        <div className="platform-subscription-empty">

                                            <FiPackage />


                                            <strong>
                                                No enabled modules
                                            </strong>


                                            <span>
                                                No module access is currently available.
                                            </span>

                                        </div>
                                    )
                            }

                        </section>


                        {/* ========================================================
                            BUSINESS
                        ======================================================== */}

                        <section className="platform-subscription-details-card platform-subscription-details-wide">

                            <div className="platform-subscription-details-card-header">

                                <div>
                                    <FiBriefcase />
                                </div>


                                <div>

                                    <h2>
                                        Business
                                    </h2>


                                    <p>
                                        Tenant associated with this subscription.
                                    </p>

                                </div>

                            </div>


                            <div className="platform-subscription-business-details">

                                <DetailItem
                                    icon={
                                        FiBriefcase
                                    }

                                    label="Business Name"

                                    value={
                                        business
                                            ?.name
                                    }
                                />


                                <DetailItem
                                    icon={
                                        FiBriefcase
                                    }

                                    label="Business Code"

                                    value={
                                        business
                                            ?.code
                                    }
                                />


                                <DetailItem
                                    icon={
                                        FiMonitor
                                    }

                                    label="Subdomain"

                                    value={
                                        business
                                            ?.subdomain
                                    }
                                />


                                <DetailItem
                                    icon={
                                        FiCheckCircle
                                    }

                                    label="Business Status"
                                >

                                    <BusinessStatusBadge
                                        status={
                                            business
                                                ?.status
                                        }
                                    />

                                </DetailItem>

                            </div>


                            <button
                                type="button"

                                className="platform-subscription-view-business"

                                onClick={
                                    () =>
                                        history.push(
                                            `/platform/businesses/${
                                                business
                                                    ?._id ||
                                                business
                                                    ?.id
                                            }`
                                        )
                                }
                            >

                                <FiBriefcase />

                                Open Business Details

                            </button>

                        </section>

                    </div>


                    {/* ============================================================
                        MANAGE SUBSCRIPTION
                    ============================================================ */}

                    <BusinessModal
                        open={
                            showEditModal
                        }

                        title={
                            currentSubscription
                                ?
                                "Change Subscription Plan"
                                :
                                "Assign Subscription Plan"
                        }

                        description={
                            `Select the subscription plan for ${
                                business
                                    ?.name ||
                                "this business"
                            }.`
                        }

                        size="md"

                        onClose={
                            handleCloseEdit
                        }

                        disableClose={
                            assignPlanLoading
                        }
                    >

                        <div className="platform-subscription-modal-body">

                            {/* ====================================================
                                BUSINESS
                            ==================================================== */}

                            <div className="platform-subscription-selected-business">

                                <div>

                                    {
                                        String(
                                            business
                                                ?.name ||
                                            "B"
                                        )
                                            .charAt(
                                                0
                                            )
                                            .toUpperCase()
                                    }

                                </div>


                                <section>

                                    <strong>
                                        {
                                            business
                                                ?.name ||
                                            "-"
                                        }
                                    </strong>


                                    <span>
                                        {
                                            business
                                                ?.code ||
                                            "-"
                                        }
                                    </span>

                                </section>

                            </div>


                            {/* ====================================================
                                CURRENT PLAN
                            ==================================================== */}

                            {
                                currentSubscription
                                    ?
                                    (
                                        <div className="platform-subscription-current-plan-box">

                                            <div>

                                                <span>
                                                    Current Plan
                                                </span>


                                                <strong>
                                                    {
                                                        currentPlanName
                                                    }
                                                </strong>

                                            </div>


                                            <BusinessStatusBadge
                                                status={
                                                    subscriptionStatus
                                                }
                                            />

                                        </div>
                                    )
                                    :
                                    null
                            }


                            <form
                                className="platform-subscription-form"

                                onSubmit={
                                    handleSave
                                }
                            >

                                {/* ================================================
                                    PLAN
                                ================================================ */}

                                <div className="platform-subscription-form-group">

                                    <label>
                                        Subscription Plan
                                    </label>


                                    <select
                                        name="planId"

                                        value={
                                            subscriptionForm
                                                .planId
                                        }

                                        onChange={
                                            handlePlanChange
                                        }

                                        disabled={
                                            plansLoading ||
                                            assignPlanLoading
                                        }

                                        required
                                    >

                                        <option value="">

                                            {
                                                plansLoading
                                                    ?
                                                    "Loading plans..."
                                                    :
                                                    "Select Plan"
                                            }

                                        </option>


                                        {
                                            subscriptionPlans
                                                .filter(
                                                    (
                                                        plan
                                                    ) =>
                                                        String(
                                                            plan
                                                                ?.status ||
                                                            ""
                                                        )
                                                            .trim()
                                                            .toUpperCase() ===
                                                        "ACTIVE"
                                                )
                                                .map(
                                                    (
                                                        plan
                                                    ) => (

                                                        <option
                                                            key={
                                                                plan._id
                                                            }

                                                            value={
                                                                plan._id
                                                            }
                                                        >

                                                            {
                                                                `${plan.name} (${plan.code})`
                                                            }

                                                        </option>

                                                    )
                                                )
                                        }

                                    </select>

                                </div>


                                {/* ================================================
                                    BILLING
                                ================================================ */}

                                <div className="platform-subscription-form-group">

                                    <label>
                                        Billing Cycle
                                    </label>


                                    <select
                                        name="billingCycle"

                                        value={
                                            subscriptionForm
                                                .billingCycle
                                        }

                                        onChange={
                                            handleSubscriptionChange
                                        }

                                        disabled={
                                            !selectedSubscriptionPlan ||
                                            selectedPlanType ===
                                                "TRIAL" ||
                                            assignPlanLoading
                                        }
                                    >

                                        {
                                            selectedPlanType ===
                                                "TRIAL"
                                                ?
                                                (
                                                    <option value="TRIAL">
                                                        Trial
                                                    </option>
                                                )
                                                :
                                                null
                                        }


                                        {
                                            availableBillingCycles
                                                .includes(
                                                    "MONTHLY"
                                                )
                                                ?
                                                (
                                                    <option value="MONTHLY">
                                                        Monthly
                                                    </option>
                                                )
                                                :
                                                null
                                        }


                                        {
                                            availableBillingCycles
                                                .includes(
                                                    "YEARLY"
                                                )
                                                ?
                                                (
                                                    <option value="YEARLY">
                                                        Yearly
                                                    </option>
                                                )
                                                :
                                                null
                                        }

                                    </select>

                                </div>


                                {/* ================================================
                                    PLAN PRICE
                                ================================================ */}

                                {
                                    selectedSubscriptionPlan
                                        ?
                                        (
                                            <div className="platform-subscription-selected-plan-summary">

                                                <div>

                                                    <span>
                                                        Selected Plan
                                                    </span>


                                                    <strong>
                                                        {
                                                            selectedSubscriptionPlan
                                                                ?.name
                                                        }
                                                    </strong>


                                                    <small>
                                                        {
                                                            selectedSubscriptionPlan
                                                                ?.code
                                                        }
                                                    </small>

                                                </div>


                                                <div>

                                                    {
                                                        selectedPlanType ===
                                                            "TRIAL"
                                                            ?
                                                            (
                                                                <strong>
                                                                    Free Trial
                                                                </strong>
                                                            )
                                                            :
                                                            (
                                                                <strong>

                                                                    ₹{
                                                                        formatPrice(
                                                                            subscriptionForm
                                                                                .billingCycle ===
                                                                            "YEARLY"
                                                                                ?
                                                                                selectedSubscriptionPlan
                                                                                    ?.yearlyPrice
                                                                                :
                                                                                selectedSubscriptionPlan
                                                                                    ?.monthlyPrice
                                                                        )
                                                                    }

                                                                    {
                                                                        subscriptionForm
                                                                            .billingCycle ===
                                                                        "YEARLY"
                                                                            ?
                                                                            " / year"
                                                                            :
                                                                            " / month"
                                                                    }

                                                                </strong>
                                                            )
                                                    }


                                                    <span>
                                                        Payment Waived
                                                    </span>

                                                </div>

                                            </div>
                                        )
                                        :
                                        null
                                }


                                {/* ================================================
                                    MODULES
                                ================================================ */}

                                {
                                    selectedSubscriptionPlan
                                        ?
                                        (
                                            <div className="platform-subscription-modal-modules">

                                                <label>
                                                    Included Modules
                                                </label>


                                                {
                                                    selectedPlanModules.length
                                                        ?
                                                        (
                                                            <div>

                                                                {
                                                                    selectedPlanModules
                                                                        .map(
                                                                            (
                                                                                module
                                                                            ) => (

                                                                                <span
                                                                                    key={
                                                                                        module
                                                                                            ?.code
                                                                                    }
                                                                                >

                                                                                    <FiCheckCircle />

                                                                                    {
                                                                                        module
                                                                                            ?.name ||
                                                                                        module
                                                                                            ?.code
                                                                                    }

                                                                                </span>

                                                                            )
                                                                        )
                                                                }

                                                            </div>
                                                        )
                                                        :
                                                        (
                                                            <small>
                                                                No modules are enabled in this plan.
                                                            </small>
                                                        )
                                                }

                                            </div>
                                        )
                                        :
                                        null
                                }


                                {/* ================================================
                                    REASON
                                ================================================ */}

                                <div className="platform-subscription-form-group platform-subscription-form-group-full">

                                    <label>
                                        Assignment Reason
                                    </label>


                                    <textarea
                                        name="assignmentReason"

                                        value={
                                            subscriptionForm
                                                .assignmentReason
                                        }

                                        onChange={
                                            handleSubscriptionChange
                                        }

                                        placeholder="Example: Complimentary Gold access for partner business"

                                        rows={
                                            3
                                        }

                                        disabled={
                                            assignPlanLoading
                                        }
                                    />


                                    <small>
                                        Optional internal note. Payment will not be required.
                                    </small>

                                </div>


                                {/* ================================================
                                    API ERROR
                                ================================================ */}

                                {
                                    assignPlanState
                                        ?.error
                                        ?
                                        (
                                            <div className="platform-subscription-assign-error">

                                                {
                                                    assignPlanState
                                                        ?.error
                                                        ?.message ||
                                                    "Unable to assign subscription plan."
                                                }

                                            </div>
                                        )
                                        :
                                        null
                                }


                                {/* ================================================
                                    ACTIONS
                                ================================================ */}

                                <div className="platform-subscription-form-actions">

                                    <button
                                        type="button"

                                        onClick={
                                            handleCloseEdit
                                        }

                                        disabled={
                                            assignPlanLoading
                                        }
                                    >
                                        Cancel
                                    </button>


                                    <button
                                        type="submit"

                                        className="primary"

                                        disabled={
                                            assignPlanLoading ||
                                            plansLoading ||
                                            !subscriptionForm
                                                .planId
                                        }
                                    >

                                        {
                                            assignPlanLoading
                                                ?
                                                "Saving..."
                                                :
                                                currentSubscription
                                                    ?
                                                    "Change Plan"
                                                    :
                                                    "Assign Plan"
                                        }

                                    </button>

                                </div>

                            </form>

                        </div>

                    </BusinessModal>

                </div>


        );

    };


export default
    PlatformSubscriptionDetails;